import { useGetStorefrontEditLeaseQuery } from '../store/endpoints/StorefrontLeaseEndpoints';

// How often an open storefront editor asks whether the draft is still free. The assistant's turns run
// for tens of seconds, so a few seconds of lag either side of a lock is fine, and the request is one
// key read on the API. Short enough that the editor unlocks itself promptly when a turn finishes.
const POLL_INTERVAL_MS = 5000;

export interface IStorefrontEditLease {
    // True while somebody else holds this storefront's edit lease. This is the flag the editors thread
    // through their forms as read-only.
    locked: boolean;
    // The holder token (assistant session id) when locked.
    holder?: string;
    // True when the hold belongs to the AI assistant, which is every hold today. The banner copy keys
    // off this so it can stay accurate if another background writer ever starts leasing.
    heldByAssistant: boolean;
}

/**
 * Polls whether one storefront's draft is locked by an assistant turn.
 *
 * Everything unknown resolves to unlocked: no storefront id, the first load before an answer arrives,
 * or a failed request. That is the deliberate direction to be wrong in. A false "locked" freezes an
 * editor that nothing is actually touching, while a false "unlocked" costs at most one save that comes
 * back 409 and is reported as such. The API makes the same fail-open choice on its side.
 */
const useStorefrontEditLease = (storefrontId?: string): IStorefrontEditLease => {
    const lease = useGetStorefrontEditLeaseQuery(storefrontId ?? '', {
        skip: !storefrontId,
        pollingInterval: POLL_INTERVAL_MS,
        // Opening an editor asks fresh rather than trusting a cached answer from a previous visit; a
        // lease from a minute ago says nothing about now. (refetchOnFocus is not an option here: the
        // app never calls setupListeners, so it would be silently inert.)
        refetchOnMountOrArgChange: true,
    });

    return {
        locked: lease.data?.locked ?? false,
        holder: lease.data?.holder,
        heldByAssistant: lease.data?.heldByAssistant ?? false,
    };
};

export default useStorefrontEditLease;
